// Emissão do boleto agrupado (#5785) — as decisões antes de o boleto existir.
//
// A sequência de emissão em si (criar a fatura AVULSO, registrar no Sicoob, conferir que o título
// existe) é a de `emitirCobrancaAvulsa.ts`; o banco reconfere tudo nas RPCs
// preparar/vincular/concluir (migration 20260921150000). Aqui ficam só as contas e os campos,
// sem I/O: roda sob `node --test` com type-stripping.
//
// Regras de 21/09/2026:
//   - O título é registrado pelo valor CHEIO e a retenção vira ABATIMENTO, de modo que a Prefeitura
//     pague o líquido (decisão da demanda 03, resolvida na 16 em 26/09/2026). A API v3 do Sicoob não
//     tem pagamento divergente nem valor mínimo — a coleção oficial não traz esses campos —, e
//     `valorAbatimento` é o único caminho para o pagador pagar menos que o nominal sem o banco
//     recusar no vencimento.
//   - Total R$ 0,00 não registra título (a Prefeitura recebe o documento mesmo assim).
//   - A conta Sicoob é a do agrupamento, nunca deduzida da UC.
//   - O Sicoob exige endereço completo do pagador: sem ele, recusar antes de chamar o banco.

import { isContaSicoob } from './contaSicoob.ts';

export interface ValoresBoletoAgrupado {
  /** Total do fechamento: o valor NOMINAL do título. */
  bruto: number;
  /** Imposto que a Prefeitura retém. */
  retencao: number;
  /** Valor registrado no banco — o cheio, igual ao bruto. */
  cobrado: number;
  /** O que vai no `valorAbatimento` do Sicoob. Mesmo número da retenção. */
  abatimento: number;
  /** O que a Prefeitura efetivamente paga: nominal menos abatimento. */
  aPagar: number;
}

const centavos = (v: unknown): number => {
  const n = typeof v === 'string' ? Number(v) : v;
  return typeof n === 'number' && Number.isFinite(n) ? Math.round(n * 100) : 0;
};

/**
 * Mesma conta do banco (RPC `preparar_emissao_agrupamento`): retenção = round(bruto × % / 100, 2).
 * O título é registrado pelo bruto e a retenção vai como abatimento — quem paga desembolsa `aPagar`.
 */
export function calcularValoresBoleto(totalVigor: unknown, retencaoPercentual: unknown = 0): ValoresBoletoAgrupado {
  const bruto = Math.max(centavos(totalVigor), 0);
  const pctBruto = typeof retencaoPercentual === 'string' ? Number(retencaoPercentual) : retencaoPercentual;
  const pct = typeof pctBruto === 'number' && Number.isFinite(pctBruto) ? Math.min(Math.max(pctBruto, 0), 99.99) : 0;
  // Abatimento igual ao nominal faz o Sicoob recusar o registro: nesse caso não há o que abater.
  const retencao = Math.min(Math.round((bruto * pct) / 100), Math.max(bruto - 1, 0));
  return {
    bruto: bruto / 100,
    retencao: retencao / 100,
    cobrado: bruto / 100,
    abatimento: retencao / 100,
    aPagar: (bruto - retencao) / 100,
  };
}

export interface AgrupamentoParaEmissao {
  id: number;
  nome: string;
  cnpj_pagador: string;
  pagador_nome?: string | null;
  pagador_logradouro?: string | null;
  pagador_cidade?: string | null;
  pagador_uf?: string | null;
  pagador_cep?: string | null;
  conta_sicoob?: string | null;
  retencao_percentual?: number | string | null;
  codigos?: string[] | null;
}

export interface FechamentoParaEmissao {
  id: number;
  status: string;
  total_vigor?: number | string | null;
  fatura_boleto_id?: number | null;
}

/**
 * Por que não dá para emitir, ou null quando pode. A tela mostra a frase em vez de só deixar o
 * botão cinza; o banco reconfere as mesmas regras na RPC de preparar.
 */
export function motivoNaoPodeEmitir(ag: AgrupamentoParaEmissao, fech: FechamentoParaEmissao | null): string | null {
  if (!fech) return 'O mês ainda não foi aberto para este agrupamento.';
  if (fech.status === 'emitido') return null;
  if (fech.status !== 'fechado') return 'Feche o mês antes de emitir o boleto.';
  const { cobrado } = calcularValoresBoleto(fech.total_vigor, ag.retencao_percentual);
  // Zerado não fala com o banco: não precisa de conta nem de endereço.
  if (cobrado <= 0) return null;
  if (!isContaSicoob(ag.conta_sicoob)) {
    return 'Defina a conta Sicoob do agrupamento (em Editar) antes de emitir.';
  }
  const faltando = [
    !String(ag.pagador_logradouro ?? '').trim() && 'endereço',
    !String(ag.pagador_cidade ?? '').trim() && 'cidade',
    !String(ag.pagador_uf ?? '').trim() && 'UF',
    String(ag.pagador_cep ?? '').replace(/\D/g, '').length !== 8 && 'CEP',
  ].filter(Boolean);
  if (faltando.length) return `Complete os dados do pagador (${faltando.join(', ')}) em Editar: o banco recusa boleto sem endereço.`;
  return null;
}

export interface EntradaCamposBoleto {
  agrupamento: AgrupamentoParaEmissao;
  /** Competência no formato do resto do sistema, "AGO/2026". */
  competencia: string;
  valorCobrado: number;
  /** Retenção que vira `valorAbatimento` no Sicoob. Zero quando o agrupamento não retém. */
  valorAbatimento?: number;
  /** "YYYY-MM-DD", da régua do agrupamento (#5787). */
  vencimento: string;
  ucsCobradas: number;
}

/** Campos do INSERT da fatura AVULSO que é o boleto agrupado. */
export function montarCamposBoletoAgrupado({ agrupamento: ag, competencia, valorCobrado, valorAbatimento = 0, vencimento, ucsCobradas }: EntradaCamposBoleto) {
  const cnpj = String(ag.cnpj_pagador ?? '').replace(/\D/g, '');
  const nome = String(ag.pagador_nome ?? '').trim() || ag.nome;
  const codigo = (ag.codigos ?? []).filter(Boolean).join('/');
  const descricao = `Fatura agrupada ${ag.nome}${codigo ? ` (agrupamento ${codigo})` : ''} — competência ${competencia}, ${ucsCobradas} UC(s)`;
  return {
    // Não é UC: identifica o agrupamento. A fatura AVULSO fica fora da classificação por UC.
    uc_codigo: codigo || `AGRUPAMENTO-${ag.id}`,
    cliente_nome: nome,
    cliente_cpf_cnpj: cnpj,
    nome_cliente_boleto: nome,
    cpf_cnpj_cliente_boleto: cnpj,
    end_logradouro: String(ag.pagador_logradouro ?? '').trim(),
    end_numero: '',
    end_bairro: '',
    end_cep: String(ag.pagador_cep ?? '').replace(/\D/g, ''),
    end_cidade: String(ag.pagador_cidade ?? '').trim(),
    end_uf: String(ag.pagador_uf ?? '').trim().toUpperCase(),
    mes_referencia: competencia,
    valor_boleto_vigor: valorCobrado,
    vencimento_boleto: vencimento,
    data_vencimento: vencimento,
    valor_parcelamentos_boleto: valorCobrado,
    // Viaja no payload do n8n até o `valorAbatimento` do Sicoob: o título fica cheio e a Prefeitura
    // paga o líquido. Abatimento inválido (≥ nominal) é descartado pelo fluxo.
    valor_abatimento_boleto: valorAbatimento > 0 && valorAbatimento < valorCobrado ? valorAbatimento : 0,
    parcelamento_descricao: descricao,
    total_com_vigor: 0,
    economia: 0,
    custo_s_vigor: 0,
    observacoes_boleto: `${descricao}. A relação das unidades acompanha este boleto.`,
  };
}
